import { useState } from "react";
import { ApiError, type InstanceInsights } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { StatusDot } from "../components/primitives";

// AI insight layer (KEY-163): an on-demand, read-only analysis of this dial. Advisory only — nothing is
// written back. The server gates this by a feature flag and the org's opt-in; this panel handles the
// "not enabled / not configured" responses gracefully.

const SEVERITY: Record<string, { label: string; colour: string }> = {
  urgent: { label: "Urgent", colour: "var(--st-red, #c0392b)" },
  watch: { label: "Watch", colour: "var(--st-amber, #b45309)" },
  info: { label: "Info", colour: "var(--text-faint, #64748b)" },
};

const TREND: Record<string, string> = {
  improving: "↑ improving",
  stable: "→ stable",
  deteriorating: "↓ deteriorating",
};

export function InsightsPanel({ instanceId }: { instanceId: string }) {
  const [result, setResult] = useState<InstanceInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    const token = getAccessToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.analyseInstance(token, instanceId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError("AI insights aren't switched on for this organisation. An administrator can enable them.");
      } else if (e instanceof ApiError && e.status === 503) {
        setError("AI insights aren't configured on this server yet.");
      } else if (e instanceof ApiError && e.status === 404) {
        setError("AI insights aren't available here.");
      } else {
        setError(e instanceof ApiError ? e.message : "Analysis couldn't run. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" }}>AI insights</h3>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
            An advisory read of this dial's current state and recent changes. AI-generated — verify before acting; nothing is changed.
          </p>
        </div>
        <button
          onClick={() => void run()}
          disabled={loading}
          className="kd-focusable"
          style={{
            font: "inherit",
            padding: "9px 15px",
            borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--brand-600, #4338ca)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13.5,
            cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.7 : 1,
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "Analysing…" : result ? "Re-analyse" : "Analyse this dial"}
        </button>
      </div>

      {error && (
        <p style={{ marginTop: 14, marginBottom: 0, background: "var(--surface-2)", border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius-sm)", padding: "10px 14px", color: "var(--text-muted)", fontSize: 13.5 }}>
          {error}
        </p>
      )}

      {result && result.findings.length === 0 && (
        <p style={{ marginTop: 14, marginBottom: 0, color: "var(--text-muted)", fontSize: 14 }}>
          No issues surfaced — this dial looks on track.
        </p>
      )}

      {result && result.findings.length > 0 && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {result.findings.map((f, i) => {
            const sev = SEVERITY[f.severity] ?? SEVERITY.info;
            return (
              <div
                key={i}
                style={{
                  border: "var(--border-w) solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-2)",
                  padding: "12px 14px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                  <StatusDot colour={sev.colour} size={9} />
                  <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{f.nodeRef}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: sev.colour, textTransform: "uppercase", letterSpacing: "0.04em" }}>{sev.label}</span>
                  <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{TREND[f.trend] ?? f.trend}</span>
                </div>
                <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--text)", lineHeight: 1.5 }}>{f.observation}</p>
                {f.recommendedAction && (
                  <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "var(--text-muted)" }}>
                    <strong style={{ color: "var(--text)" }}>Recommended:</strong> {f.recommendedAction}
                    {f.owner ? ` · Owner: ${f.owner}` : ""}
                    {f.targetDate ? ` · By ${f.targetDate}` : ""}
                  </p>
                )}
              </div>
            );
          })}
          <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "var(--text-faint)" }}>
            Generated by {result.model}. AI-generated — review before acting.
          </p>
        </div>
      )}
    </div>
  );
}
